/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * OnboardingShell — the shared full-screen layout for every phone-first
 * onboarding step (phone → OTP → profile → emergency → discovery).
 *
 * Chrome (per the Batch-2 design spec):
 *   - lavender background (#F3F1FB) with soft decorative circles
 *   - top progress bar + optional back chevron
 *   - scrollable content region
 *   - bottom primary CTA (sticky), safe-area aware
 *
 * It composes the existing app primitives (Button) and design language rather
 * than inventing a parallel system. The decorative circles are aria-hidden and
 * purely visual — every screen still works if they are removed.
 */

import type { ReactNode } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  /** Current step (1-based) for the progress bar. */
  step: number;
  /** Total steps in the onboarding flow, for the progress bar. */
  totalSteps: number;
  /** Back chevron handler. Omit to hide the chevron (e.g. first step). */
  onBack?: () => void;
  /** Main scrollable content. */
  children: ReactNode;
  /** Bottom primary CTA label. Omit the whole CTA block by omitting this. */
  ctaLabel?: ReactNode;
  onCta?: () => void;
  ctaDisabled?: boolean;
  ctaBusy?: boolean;
  /** Optional secondary action under the CTA (e.g. "I'll do this later"). */
  secondaryLabel?: ReactNode;
  onSecondary?: () => void;
  /** Accessible label for the screen region. */
  ariaLabel?: string;
}

export default function OnboardingShell({
  step,
  totalSteps,
  onBack,
  children,
  ctaLabel,
  onCta,
  ctaDisabled,
  ctaBusy,
  secondaryLabel,
  onSecondary,
  ariaLabel,
}: Props) {
  const pct = Math.max(0, Math.min(100, (step / totalSteps) * 100));

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className="fixed inset-0 z-[1000] flex flex-col bg-[#F3F1FB] text-[#2A2342] overflow-hidden"
    >
      {/* Soft decorative circles — purely visual, aria-hidden. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-20 h-64 w-64 rounded-full bg-[#E3DCF7] opacity-60 blur-[2px]" />
        <div className="absolute top-1/3 -left-24 h-56 w-56 rounded-full bg-[#EDE7FB] opacity-70" />
        <div className="absolute -bottom-28 -right-10 h-72 w-72 rounded-full bg-[#E9E2F9] opacity-50" />
      </div>

      {/* Top bar: back chevron + progress. */}
      <div className="relative z-10 flex items-center gap-3 px-5 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-2">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="-ml-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#6B5FA0] active:scale-95 transition-transform"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
        ) : (
          <span className="h-11 w-11 shrink-0" aria-hidden="true" />
        )}
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E0D8F3]"
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-label={`Step ${step} of ${totalSteps}`}
        >
          <div
            className="h-full rounded-full bg-[#7C52F5] transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="h-11 w-11 shrink-0" aria-hidden="true" />
      </div>

      {/* Scrollable content. */}
      <div className="relative z-10 flex-1 overflow-y-auto px-6 pt-4 pb-6">
        {children}
      </div>

      {/* Bottom CTA. */}
      {(ctaLabel || secondaryLabel) && (
        <div className="relative z-10 px-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3">
          {ctaLabel && (
            <Button
              type="button"
              onClick={onCta}
              disabled={ctaDisabled || ctaBusy}
              className="h-[55px] w-full rounded-2xl bg-[#7C52F5] text-base font-semibold text-white shadow-[0_14px_30px_-10px_rgba(124,82,245,0.6)] hover:bg-[#6B45E0] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
            >
              {ctaBusy ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                ctaLabel
              )}
            </Button>
          )}
          {secondaryLabel && (
            <button
              type="button"
              onClick={onSecondary}
              className="mt-2 min-h-[44px] w-full py-2 text-sm font-medium text-[#6B5FA0] active:opacity-70"
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
